
import { Prize } from './prize';
import { Sponsor } from './sponsor';
import { DateTimeHelper } from 'src/app/core/helper/datetime.helper';
import { IRaffleWinner } from './interfaces';
import { getCurrencySymbol } from '@angular/common';
import { PricingTier } from './pricing-tier';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { Entity } from 'src/app/core/data/schema/entity';


export class Raffle extends Entity {

    static currentRaffle: Raffle;

    static className = 'Raffle';
    static idField = 'raffleId';

    raffleId: number;
    userId: number;
    name = '';
    description: string;
    saleStart: string;
    saleEnd: string;
    logoPath: string;
    bannerPath: string;
    licenseNumber: string;
    licensePath: string;
    raffleType: RaffleType;
    drawType: RaffleDrawType = RaffleDrawType.Manual;
    country: string;
    province: string;
    minAge: number;
    organizationName: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    lastModified: string;
    fee: number | null;
    stripeAccountEmail: string;
    stripeAccountId: string;
    paymentUserId: number | null;
    lastModifiedBy: number;
    status: RaffleStatus = RaffleStatus.Draft;
    createdBy: number;
    accessCode = '';
    dateCreated: string;
    lastModBy: number | null;
    lastModOn: string | null;
    startTicketNumber: number;
    auctionAccessCode: string;
    rules = '';
    organizationWebsite: string;
    prizeClaimInstructions: string;
    inviteEmailText: string;
    raffleInvites: string;
    linkFb: string;
    linkLinkedin: string;
    linkYoutube: string;
    linkTwitter: string;
    linkInstagram: string;
    communityId: number;
    communityName: string;

    charitableOrganizationNumber: string;

    brandingColor = '#1A3250';
    timezone = 'unset';

    prizes: Prize[] = [] = [];
    sponsors: Sponsor[] = [];
    winners: IRaffleWinner[] = [];
    pricingTiers: PricingTier[] = [];

    showSellerLeaderboard = false;
    requireSellerSelection = false;
    showAmountRaised = false;

    // properties from here on down are non-db properties, added by mapping or otherwise
    isPrivate: boolean;
    showPurchaserName: boolean = true;
    totalValue: number;
    raffleLocation: string;

    totalSales: number;
    totalFee: number;
    totalDeposited: number;
    totalNumTickets: number;

    totalRaised: number;
    ticketsSold: number;
    currency = '';

    creatorEmail: string;
    creatorPhone = ''; // satisfy change detection; remove type annotation
    creatorName: string;

    pastInvites: string[] = [];
    statusString: string;

    currencySymbol = '$';

    startObj: Date;
    endObj: Date;

    // Approval
    noLicenseReason: string;
    rejectReason: string;

    // CBS Properties
    fundraisingCampaignId?: number;


    private startFmt: string;
    private endFmt: string;

    public constructor(init?: Partial<Raffle>) {

        super(Raffle.className, Raffle.idField);

        Object.assign(this, init);

        this.refreshDisplayDates();
        if (this.raffleInvites) {
            this.pastInvites = this.raffleInvites.split(',');
        }

        this.prizes = this.prizes.map(m => new Prize(m));
        this.pricingTiers = this.pricingTiers.map(m => new PricingTier(m));
        this.sponsors = this.sponsors.map(m => new Sponsor(m));

        // for prizes, sort by sortOrder
        this.prizes.sort((a, b) => a.sortOrder - b.sortOrder);
        this.sponsors.sort((a, b) => a.sortOrder - b.sortOrder);

        this.currency = this.currency.toUpperCase();
        this.country = (this.country != null) ? this.country.toUpperCase() : null;
        this.province = (this.province != null) ? this.province.toUpperCase() : null;

        this.currencySymbol = getCurrencySymbol(this.currency, 'narrow');

        switch (this.status) {
            case RaffleStatus.Draft: this.statusString = 'Draft'; break;
            case RaffleStatus.Active: this.statusString = 'Active'; break;
            case RaffleStatus.Drawn: this.statusString = 'Closed'; break;
            case RaffleStatus.Deleted: this.statusString = 'Deleted'; break;
            case RaffleStatus.Stale: this.statusString = 'Stale'; break;
            case RaffleStatus.Pending: this.statusString = 'In Review'; break;
        }

        if (String.isNullOrWhiteSpace(this.brandingColor)) {
            this.brandingColor = '#1A3250';
        }
    }

    static get currentRaffleId() { return this.currentRaffle?.raffleId; }

    get startDisplay() { return this.startFmt; }
    get endDisplay() { return this.endFmt; }

    get hasStarted() {
        return this.status >= RaffleStatus.Active &&
            (this.startObj != null && this.startObj.valueOf() <= new Date().valueOf());
    }

    get isLive() {
        return this.hasStarted === true &&
            (this.endObj != null && this.endObj.valueOf() > new Date().valueOf());
    }

    get where() { return this.country + ' ' + (this.province ?? ''); }

    get url() { return `${AppSettings.clientURL}/${this.accessCode}`; }


    public static async fetchAll() {
        return await Raffle.fetchArray(Raffle, null);
    }

    public static async fetchAllForL57Dash() {
        return await Raffle.fetchArray(Raffle, null, 'all');
    }


    lastDrawDate() {
        let drawDate = 'Not set';
        if (this.prizes.length > 0) {
            const d = this.prizes[this.prizes.length - 1].drawDate;
            drawDate = DateTimeHelper.formatDate(new Date(d));
        }
        return drawDate;
    }

    private refreshDisplayDates() {

        if (this.saleStart === '0001-01-01T00:00:00') {
            this.saleStart = undefined;
        }
        else if (this.saleStart != null) {
            // check if sale start has a Z on the end, if not we add it
            if (!this.saleStart.endsWith('Z')) {
                this.saleStart = this.saleStart + 'Z';
            }

            this.startObj = new Date(this.saleStart);
            this.startFmt = DateTimeHelper.formatDate(this.saleStart);
        }

        if (this.saleEnd === '0001-01-01T00:00:00') {
            this.saleEnd = undefined;
        }
        else if (this.saleEnd != null) {
            // check if sale end has a Z on the end, if not we add it
            if (!this.saleEnd.endsWith('Z')) {
                this.saleEnd = this.saleEnd + 'Z';
            }

            this.endObj = new Date(this.saleEnd);
            this.endFmt = DateTimeHelper.formatDate(this.saleEnd);
        }
    }
}

export enum RaffleType {
    Cash = 1,
    FiftyFifty = 2,
    Prize = 3
}

export enum RaffleStatus {
    Draft = 1,
    Active = 2, // live
    Pending = 3, // doesn't exist anymore
    Drawn = 4, // ended
    Deleted = 5,
    Stale = 6
}

export enum RaffleDrawType {
    Manual = 1,
    Digital = 2,
    other = 3
}

export enum RaffleAccessLevel {
    SuperAdmin = 50,
    RaffleCreator = 30,
    RaffleAdmin = 20
}

